import { ManifestStatus } from './../../model/SolutionInfo';
import { SolutionService } from 'src/app/service/solution.service';
import { GeneralService } from 'src/app/service/general.service';
import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { EventService } from 'src/app/service/event.service';
import { ModalMessageComponent, ModalMessageType } from '../controls/modal-message/modal-message.component';
import { Modal } from '../controls/modal/modal.service';
import { LoginProxyComponent, ProxyModalConfig, ProxyModalResult } from '../dialogs/login-proxy/login-proxy.component';
import { catchError, finalize, switchMap, tap } from 'rxjs/operators';
import { Utils } from 'src/app/utils';
import { Events } from 'src/app/service/websocket.service';
import { UserInfo, UserRole } from 'src/app/model/User';
import { AuthenticationService } from 'src/app/service/authentication.service';
import { of, Subscription } from 'rxjs';
import { DragDropComponent } from '../dialogs/drag-drop/drag-drop.component';
import { UploadFileType, UploadStatus } from 'src/app/model/Upload';
import { DataTableRow } from '../controls/datatable/datatable.component';
import { ModalConfig } from '../controls/modal/modal-config';

@Component({
    selector: 'app-settings-manifest',
    templateUrl: './settings-manifest.component.html',
    styleUrls: ['../settings/settings.component.css', './settings-manifest.component.css'],
    standalone: false
})
export class SettingsManifestComponent implements OnInit {

  isLoading:boolean = false;
  isSaving:boolean = false;
  manifestStatus:ManifestStatus;
  savingBtn:string = $localize`Save`;
  currentUserAuthInfo : UserInfo;
  isOnlinemode:boolean;
  isUpdatingManifest:boolean;
  UserRole = UserRole;
  canEdit:boolean = false;

  eventMessageSubscription: Subscription;
  
  settingsForm = new FormGroup({
    autoUpdateCtrl: new FormControl(),
    baseURLCtrl: new FormControl('', [
      Validators.required,
      Validators.maxLength(200),
      Validators.pattern('^(http|https)://.+$')
    ])
  });
  
  constructor(private eventService: EventService,
              private modal: Modal,
              private genService: GeneralService,
              private authService: AuthenticationService,
              private solService: SolutionService) { }

  get autoUpdateCtrl() { return this.settingsForm.get('autoUpdateCtrl'); }
  get baseURLCtrl() { return this.settingsForm.get('baseURLCtrl'); }

  ngOnInit(): void {
    this.eventMessageSubscription = this.eventService.eventMessage$.subscribe(msg => {
      if(msg.eventType == Events.ManifestUpdateStarted) {
        this.isUpdatingManifest = true;
      }else if(msg.eventType == Events.ManifestUpdateComplete){
        this.isUpdatingManifest = false;
        setTimeout(() => {
          this.getManifestStatus();
        }, 1000);
      }else if(msg.eventType == Events.SettingsConnectionMode){
        this.initData(false);
      }
    });
    this.currentUserAuthInfo = this.authService.getLoggedInUser();
    this.canEdit = this.currentUserAuthInfo.role === UserRole.SUPERUSER;
    this.initData();
  }

  ngOnDestroy(){
    this.eventMessageSubscription.unsubscribe();
  }

  initData(showLoading:boolean = true):void {
    if(showLoading){
      this.isLoading = true;
    }

    this.getManifestStatus();
  }

  getManifestStatus(): void {
    this.genService.getManifestSettings().pipe(tap(resp => {
      this.manifestStatus = resp.body;
        this.autoUpdateCtrl.setValue(this.manifestStatus.autoUpdate);
        this.baseURLCtrl.setValue(this.manifestStatus.baseURL);
        this.baseURLCtrl.updateValueAndValidity();
    }),
    switchMap(() => this.solService.getSolutionInfo(this.currentUserAuthInfo.settings.environmentId, this.currentUserAuthInfo.settings.solutionName).pipe(
      tap(resp => {
      this.isUpdatingManifest = resp.body.isUpdatingManifest;
      this.isOnlinemode = this.currentUserAuthInfo.settings.isOnlineMode;
      })
    )),
    finalize(() => {
      this.isLoading = false;
      this.isSaving = false;
      this.settingsForm.reset(this.settingsForm.value);
    })).subscribe();
  }

  checkForUpdates():void {
    if(this.isUpdatingManifest){
      this.modal.open(ModalMessageComponent, {
        data: {
          title: $localize`Warning`,
          message: $localize`You can't check for the manifest update while the system is updating manifest data. `,
          primaryBtn: $localize`OK`,
          type: ModalMessageType.Warning,
          width: '500px',
        },
        id: 'otsc-settings-manifest-updating'
      });
    }else{
      const sub: Subscription =
        this.modal.open(ModalMessageComponent, {
          data: {
            title: $localize`Check for updates`,
            message: $localize`Do you want to check for the latest manifest?`,
            primaryBtn: $localize`Yes`,
            secondaryBtn: $localize`No`,
            width: '500px',
          },
          id: 'otsc-settings-manifest-update'
        }
        ).afterClosed.subscribe((isPrimaryBtnClicked: boolean) => {
          if (isPrimaryBtnClicked) {
            if (this.currentUserAuthInfo.settings.proxyPrompt) {
              const sub: Subscription =
                this.modal.open(LoginProxyComponent, {
                  data: new ProxyModalConfig(false),
                  id: 'otsc-proxy-login-dialog',
                  maxheight: '800px'
                }
                ).afterClosed.subscribe((result: ProxyModalResult) => {
                  if (result.ok) {
                    this.checkForNewManifest(result.user, result.password);
                  }
                  sub.unsubscribe();
                });
            } else {
              this.checkForNewManifest("", "");
            }
          }
          sub.unsubscribe();
        });
    }
  }

  save():void {
    const sub: Subscription =
      this.modal.open(ModalMessageComponent, {
        data: {
          title: $localize`Save`,
          message: $localize`The changes made on this page will be saved. Do you want to save?`,
          primaryBtn: $localize`Yes`,
          secondaryBtn: $localize`No`,
          width: '350px',
        },
        id: 'otsc-settings-manifest-save'
      }
      ).afterClosed.subscribe((isPrimaryBtnClicked: boolean) => {
        if (isPrimaryBtnClicked) {
          this.isSaving = true;

          if (this.currentUserAuthInfo.settings.proxyPrompt) {
            const sub: Subscription =
              this.modal.open(LoginProxyComponent, {
                data: new ProxyModalConfig(false),
                id: 'otsc-proxy-login-dialog',
                maxheight: '800px'
              }
              ).afterClosed.subscribe((result: ProxyModalResult) => {
                if (result.ok) {
                  this.submitChanges(false, result.user, result.password);
                }
                sub.unsubscribe();
              });
          } else {
            this.submitChanges(false, "", "");
          }
        }
        sub.unsubscribe();
      });
  }

  reset(): void {
    const sub: Subscription =
      this.modal.open(ModalMessageComponent, {
        data: {
          title: $localize`Reset`,
          message: $localize`The changes made on this page will be lost. Do you want to restore defaults?`,
          primaryBtn: $localize`Yes`,
          secondaryBtn: $localize`No`,
          width: '350px',
        },
        id: 'otsc-settings-manifest-reset'
      }
      ).afterClosed.subscribe((isPrimaryBtnClicked: boolean) => {
        if (isPrimaryBtnClicked) {
          this.isSaving = true;

          if (this.currentUserAuthInfo.settings.proxyPrompt) {
            const sub: Subscription =
              this.modal.open(LoginProxyComponent, {
                data: new ProxyModalConfig(false),
                id: 'otsc-proxy-login-dialog',
                maxheight: '800px'
              }
              ).afterClosed.subscribe((result: ProxyModalResult) => {
                if (result.ok) {
                  this.submitChanges(true, result.user, result.password);
                }
                sub.unsubscribe();
              });
          } else {
            this.submitChanges(true, "", "");
          }
        }
        sub.unsubscribe();
      });
  }

  repair(): void {
    const sub: Subscription =
      this.modal.open(ModalMessageComponent, {
        data: {
          title: $localize`Repair manifest data`,
          message: $localize`The manifest data will be purged from the system and import it again. Do you wish to proceed with the manifest repair?<br/><b style="font-size:14px">This option should not be used unless directed to by Support.</b>`,
          primaryBtn: $localize`Yes`,
          secondaryBtn: $localize`No`,
          width: '500px',
          type: ModalMessageType.Warning
        },
        id: 'otsc-settings-manifest-repair'
      }
      ).afterClosed.subscribe((isPrimaryBtnClicked: boolean) => {
        if (isPrimaryBtnClicked) {
          if (this.isOnlinemode) {
            if (this.currentUserAuthInfo.settings.proxyPrompt) {
              const sub: Subscription =
                this.modal.open(LoginProxyComponent, {
                  data: new ProxyModalConfig(false),
                  id: 'otsc-proxy-login-dialog',
                  maxheight: '800px'
                }
                ).afterClosed.subscribe((result: ProxyModalResult) => {
                  if (result.ok) {
                    this.repairManifest(result.user, result.password);
                  }
                  sub.unsubscribe();
                });
            } else {
              this.repairManifest("", "");
            }
          } else {
            this.onClickDragDrop(true, true);
          }
        }
        sub.unsubscribe();
      });
  }

  submitChanges(isRestoreDefaults:boolean, username:string, password:string): void {
    this.genService.updateManifestSettings(isRestoreDefaults ? "" : this.baseURLCtrl.value, this.autoUpdateCtrl.value, username, password).pipe(tap(resp => {
      if(resp.body.completed && resp.body.success){
        if(isRestoreDefaults){
          Utils.addHeaderMessageEventBanner(this.eventService, Events.SuccessMsg, $localize`Settings were restored to defaults successfully`);
        }else{
          Utils.addHeaderMessageEventBanner(this.eventService, Events.SuccessMsg, $localize`Settings were saved successfully`);
        }
        this.initData();
      }else{
        if(resp.body.errorCode == 'ERROR_MANIFEST_TASK_URL'){
          Utils.showManifestUpdateErrorBanner(this.eventService, resp.body.dataJson);
        }else if(resp.body.errorCode == 'ERROR_PROXY_NTLM_AUTH'){
          Utils.addHeaderMessageEventBanner(this.eventService, Events.ErrorMsg, $localize`NTLM authentication is not supported. Please switch to a different authentication type.`);
        } else {
          Utils.addHeaderMessageEventBanner(this.eventService, Events.ErrorMsg, $localize`Settings could not be saved. Please try again later.`);
        }
        this.settingsForm.reset(this.settingsForm.value);
      }
    }),
    finalize(() => {
      this.isSaving = false;
      this.savingBtn = $localize`Save`;
    }),
    ).subscribe();
  }

  checkForNewManifest(username:string, password:string): void {
    this.isUpdatingManifest = true;
    this.genService.checkManifestUpdate(username, password).pipe(catchError(resp => {
      if(resp.status == 501){
        Utils.showManifestUpdateErrorBanner(this.eventService, resp.error);
      } else if(resp.status == 503){
        Utils.showManifestUpdateErrorBanner(this.eventService, resp.error.dataJson);
      }
      this.isUpdatingManifest = false;
      return of(resp);
    }),
    finalize(() => {
      this.isUpdatingManifest = false;
      this.initData(false);
    })).subscribe();
  }

  repairManifest(username:string, password:string): void {
    this.isUpdatingManifest = true;
    
    this.genService.repairManifestUpdate(username, password).pipe(tap(resp => {
      if(resp.body){
        this.initData(false);
        Utils.addHeaderMessageEventBanner(this.eventService, Events.SuccessMsg, $localize`Manifest data repair started successfully`);
      }else{
        Utils.addHeaderMessageEventBanner(this.eventService, Events.ErrorMsg, $localize`Manifest data repair could not be started. Please try again later.`);
      }
    }),
    catchError(resp => {
      if(resp.status == 501){
        Utils.showManifestUpdateErrorBanner(this.eventService, resp.error);
      } else if(resp.status == 503){
        Utils.showManifestUpdateErrorBanner(this.eventService, resp.error.dataJson);
      }
      this.isUpdatingManifest = false;
      return of(resp);
    })).subscribe();
  }

  onClickDragDrop(isShowOfflineMsg:boolean = false, isRepair: boolean = false): void {
    let desc:string = isShowOfflineMsg && !this.isOnlinemode ? $localize`The system is currently in offline mode. In order to repair the manifest in offline mode you must first upload a new manifest. Select or drop a file below to upload the manifest.` :
                                                               $localize`Select or drop a file below to upload the manifest.`;

    const sub: Subscription =
      this.modal.open(DragDropComponent, {
        data: {
          type: UploadFileType.MANIFEST,
          repair: isRepair,
          desc: desc,
          supportMultiFiles: false
        },
        id: 'otsc-drag-drop-dialog',
        customclass: 'ot-modal-side-panel ot-modal-side-panel-md',
      }).afterClosed.subscribe((files: DataTableRow[]) => {
        if (files && files.length > 0) {
          let isRequireReload: boolean = false;

          for (let i = 0; i < files.length; i++) {
            if (files[i].data.status === UploadStatus.COMPLETE) {
              isRequireReload = true;
              break;
            }
          }

          if (isRequireReload) {
            this.initData();
          }
        }
        sub.unsubscribe();
      });
  }

  canDeactivate() : boolean{
    return !this.settingsForm.dirty;
  }

  getDeactivateModalMessageConfig(): ModalConfig {
    return Utils.getDefaultDeactivateModalMessageConfig('otsc-settings-manifest-unsaved-changes');
  }
}
